<template>
  <div class="w-full border border-gray-800 mb-6">
    <div class="w-full flex p-2 bg-gray-300">
      <p class="w-1/3 text-left">
        {{ companies.join(", ") }}
        <span class="text-sm" v-if="filter">{{ filter }}</span>
      </p>
      <p class="w-1/3 text-center">
        Day {{ elapsedDaysInPeriod }} of {{ daysInPeriod }}
      </p>
      <p class="w-1/3 text-right" v-if="data.setting.monthly">Monthly</p>
      <p class="w-1/3 text-right" v-else>
        {{ startDate.format("MMM DD") }} - {{ endDate.format("MMM DD") }}
      </p>
    </div>

    <div class="w-full flex flex-wrap mt-2 px-2">
      <slide-up-down
        class="w-full"
        :class="{ 'mb-2': expanded }"
        :active="expanded"
        :duration="500"
      >
        <div class="w-full flex justify-end items-center">
          <b-button class="mr-2" type="secondary" @click="deleteItem"
            >Delete Item</b-button
          >
          <b-button @click="showUpdate">Edit Item</b-button>
        </div>
      </slide-up-down>

      <div class="w-full flex flex-wrap mb-4">
        <div class="w-1/6">&nbsp;</div>
        <div class="w-2/6 | table-heading-t">Budget Remaining</div>
        <div class="w-3/6 | table-heading-t">Over / Under</div>

        <div class="w-1/6">&nbsp;</div>
        <div class="w-1/6 | table-heading-b">Per Day</div>
        <div class="w-1/6 | table-heading-b">Total</div>
        <div class="w-1/6 | table-heading-b">Budget</div>
        <div class="w-1/6 | table-heading-b">Impressions</div>
        <div class="w-1/6 | table-heading-b">% for Media</div>

        <portal-target
          :name="'combined-stats-' + id"
          class="w-full flex flex-wrap"
        ></portal-target>
        <portal-target
          :name="'display-stats-' + id"
          class="w-full flex flex-wrap"
        ></portal-target>
        <portal-target
          :name="'video-stats-' + id"
          class="w-full flex flex-wrap"
        ></portal-target>
        <portal-target
          :name="'sem-stats-' + id"
          class="w-full flex flex-wrap"
        ></portal-target>
      </div>

      <budget-cpm-combined-item
        v-if="data.display && data.display.type === 'cpm' && data.video && data.display.type === 'cpm'"
        :data="data"
        :days-in-period="daysInPeriod"
        :elapsed-days-in-period="elapsedDaysInPeriod"
        :component-id="id"
      />

      <budget-cpm-item
        v-if="data.display && data.display.type === 'cpm'"
        :data="data"
        :expanded="expanded"
        :days-in-period="daysInPeriod"
        :elapsed-days-in-period="elapsedDaysInPeriod"
        :component-id="id"
        type="display"
      />

      <budget-agency-item
        v-if="data.display && data.display.type === 'agency'"
        :data="data"
        :expanded="expanded"
        :days-in-period="daysInPeriod"
        :elapsed-days-in-period="elapsedDaysInPeriod"
        :component-id="id"
        type="display"
      />

      <budget-cpm-item
        v-if="data.video && data.display.type === 'cpm'"
        :data="data"
        :expanded="expanded"
        :days-in-period="daysInPeriod"
        :elapsed-days-in-period="elapsedDaysInPeriod"
        :component-id="id"
        type="video"
      />

      <budget-agency-item
        v-if="data.video && data.display.type === 'agency'"
        :data="data"
        :expanded="expanded"
        :days-in-period="daysInPeriod"
        :elapsed-days-in-period="elapsedDaysInPeriod"
        :component-id="id"
        type="video"
      />

      <budget-agency-item
        v-if="data.sem"
        :data="data"
        :expanded="expanded"
        :days-in-period="daysInPeriod"
        :elapsed-days-in-period="elapsedDaysInPeriod"
        :component-id="id"
        type="sem"
      />
    </div>
    <div
      class="w-full mt-2 bg-gray-700 text-gray-200 text-sm uppercase tracking-wider text-center cursor-pointer"
      @click="expanded = !expanded"
    >
      <font-awesome-icon
        :icon="['fas', 'chevron-down']"
        class="expanded-arrow"
        :style="{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }"
      />
    </div>
  </div>
</template>

<script>
import { map } from "lodash"
import moment from "moment"
import numeral from "numeral"
import UpdatePeriodForm from "./UpdatePeriodForm"
import UpdateForm from "./UpdateForm"
import { EventBus } from "../events"
import BudgetCPMItem from "./BudgetCPMItem.vue"
import BudgetCPMCombinedItem from "./BudgetCPMCombinedItem.vue"
import BudgetAgencyItem from "./BudgetAgencyItem.vue"

export default {
  name: "BudgetItem",
  components: {
    "budget-cpm-item": BudgetCPMItem,
    "budget-agency-item": BudgetAgencyItem,
    'budget-cpm-combined-item': BudgetCPMCombinedItem
  },
  props: {
    data: {
      required: true,
    },
    allCompanies: {
      required: true,
    },
  },
  filters: {
    percent: function (value) {
      if (isNaN(value)) return ""
      return (value * 100).toFixed(2) + "%"
    },
    money: function (value) {
      if (isNaN(value)) return ""
      return numeral(value).format("$0,0.00")
    },
    number: function (value) {
      if (isNaN(value)) return ""
      return numeral(value).format("0,0")
    },
  },
  data() {
    return {
      higlights: {
        green: "green",
        yellow: "yellow",
        red: "red",
      },
      id: this._uid,
      expanded: false,
    }
  },
  computed: {
    companies() {
      return map(this.data.companies, (item) => item.name)
    },
    startDate() {
      return moment(this.data.start_date)
    },
    endDate() {
      return moment(this.data.end_date)
    },
    nowDate() {
      return moment(this.data.now)
    },
    daysInPeriod() {
      return this.endDate.diff(this.startDate, "days") + 1
    },
    elapsedDaysInPeriod() {
      let diff = this.endDate.diff(this.nowDate, "days")
      if (diff < 0) {
        return this.daysInPeriod
      } else if (diff <= this.daysInPeriod) {
        return this.daysInPeriod - diff
      } else {
        return 0
      }
    },
    filter() {
      if (!this.data.campaign_filter) return null
      if (this.data.campaign_filter_operator === "contains")
        return '(only "' + this.data.campaign_filter + '")'
      else return '(not "' + this.data.campaign_filter + '")'
    },
  },
  methods: {
    showUpdate() {
      if (!this.data.setting.monthly){
        this.$modal.show(
        UpdateForm,
        {
          companies: this.allCompanies,
          existing: this.data.setting,
          origin: 'budget_item'
        },
        {
          width: 800,
          height: "auto",
          scrollable: true,
        }
      )
      }else{
        this.$modal.show(
        UpdatePeriodForm,
        {
          companies: this.allCompanies,
          existing: this.data,
        },
        {
          width: 800,
          height: "auto",
          scrollable: true,
        }
      )
      }

    },
    deleteItem() {
      if (!this.data.setting.monthly) {
        let str =
          "This will delete the item. This cannot be undone. Continue?"
        let r = confirm(str)
        if (r == true) {
          this.axios.post("budget/destroy/" + this.data.setting.id).then(() => {
            EventBus.$emit("fetch-items")
          })
        }
      } else {
        let str =
          "This will delete the item. This cannot be undone. NOTE: this is item is part of a monthly budget setting. If deleted, it will be recreated if budget items are regenerated for this month. Continue?"
        let r = confirm(str)
        if (r == true) {
          this.axios.post("budget/period/destroy/" + this.data.id).then(() => {
            EventBus.$emit("fetch-items")
          })
        }
      }
    },
  },
}
</script>
